import { Box } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { formatMmSs } from '../../../../utilities/countdown';
import { useCountdown } from '../../../../utilities/customHooks/useCountdown';
import { Button } from '../Buttons/Button';
import { visuallyHiddenSx } from '../Fields/fieldStyles';

import type { CodeInputAlign } from './CodeInput';

export interface ResendCodeProps {
  readonly resendAt: number | null;
  readonly onResend: () => void;
  readonly busy?: boolean;
  readonly disabled?: boolean;
  readonly align?: CodeInputAlign;
}

const RESEND_SX: Record<CodeInputAlign, SystemStyleObject<Theme>> = {
  start: { alignSelf: 'flex-start', fontVariantNumeric: 'tabular-nums' },
  center: { alignSelf: 'center', fontVariantNumeric: 'tabular-nums' },
};

export const ResendCode: React.FC<ResendCodeProps> = ({ resendAt, onResend, busy = false, disabled = false, align = 'start' }) => {
  const { t } = useTranslation();
  const seconds = useCountdown(resendAt);
  const waiting = seconds > 0;
  const label = waiting
    ? t('coreComponents.codeInput.resendCountdown', 'Resend in {{time}}', { time: formatMmSs(seconds) })
    : t('coreComponents.codeInput.resend', 'Send a new code');
  return (
    <>
      <Button
        type="button"
        intent="tertiary"
        busy={busy}
        aria-disabled={waiting || disabled || undefined}
        onClick={onResend}
        sx={RESEND_SX[align]}
      >
        {label}
      </Button>
      <Box role="status" sx={visuallyHiddenSx}>
        {waiting && t('coreComponents.codeInput.sent', 'We sent you a code.')}
      </Box>
    </>
  );
};
